/**
 * DayHistoryPanel — compact audit history for a single day, shown inside
 * DayEditorPage as a collapsible "History" FieldSection.
 *
 * Queries the last 15 audit log entries for the specific audience + dayNumber.
 * Requires composite index: auditLogs (audience ASC, dayNumber ASC, timestamp DESC).
 */

import { History, Loader2, FileText, Music, Image, Copy, Activity } from 'lucide-react';
import { cn } from '@/utils/cn';
import { useDayAuditLogs } from '@/hooks/useAuditLogs';
import type { Audience } from '@/types/auth.types';
import type { AuditLog } from '@/types';
import { AUDIT_ACTION_LABELS } from '@/types/audit.types';
import { formatDisplayDate } from '@/utils/formatUtils';

// ─── Props ─────────────────────────────────────────────────────────────────────

interface DayHistoryPanelProps {
  audience:  Audience;
  dayNumber: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function HistoryIcon({ action }: { action: string }) {
  const cls = 'h-3 w-3';
  if (action.startsWith('content_cop')) return <Copy     className={cls} />;
  if (action.startsWith('content'))    return <FileText  className={cls} />;
  if (action.startsWith('audio'))      return <Music     className={cls} />;
  if (action.startsWith('image'))      return <Image     className={cls} />;
  return <Activity className={cls} />;
}

const ACTION_DOT: Record<string, string> = {
  content_created:        'bg-emerald-400',
  content_updated:        'bg-primary-400',
  content_deleted:        'bg-red-400',
  content_status_changed: 'bg-amber-400',
  content_published:      'bg-indigo-400',
  content_copied:         'bg-purple-400',
  image_uploaded:         'bg-teal-400',
  image_replaced:         'bg-teal-400',
  image_removed:          'bg-red-400',
  audio_uploaded:         'bg-teal-400',
  audio_replaced:         'bg-teal-400',
  audio_removed:          'bg-red-400',
};

function dotColor(action: string): string {
  return ACTION_DOT[action] ?? 'bg-surface-300';
}

// ─── Component ─────────────────────────────────────────────────────────────────

export default function DayHistoryPanel({ audience, dayNumber }: DayHistoryPanelProps) {
  const { data: logs, isLoading, isError } = useDayAuditLogs(audience, dayNumber);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-4 text-surface-400 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading history…
      </div>
    );
  }

  if (isError) {
    return (
      <p className="py-2 text-xs text-red-500">
        Failed to load history. A composite Firestore index may be needed — check the browser console for the Firebase link.
      </p>
    );
  }

  if (!logs || logs.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-surface-400 text-sm">
        <History className="h-4 w-4" />
        No changes recorded yet for this day.
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Vertical timeline line */}
      <div className="absolute left-3.5 top-2 bottom-2 w-px bg-surface-200" />

      <div className="space-y-0">
        {logs.map((log: AuditLog) => {
          const label = AUDIT_ACTION_LABELS[log.action] ?? log.action;
          return (
            <div key={log.id} className="relative flex items-start gap-4 py-2.5">
              {/* Dot on timeline */}
              <div className={cn(
                'shrink-0 z-10 flex h-7 w-7 items-center justify-center',
                'rounded-full border-2 border-white shadow-sm text-white',
                dotColor(log.action),
              )}>
                <HistoryIcon action={log.action} />
              </div>

              <div className="min-w-0 flex-1 pt-0.5">
                <div className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="text-xs font-semibold text-surface-800">{log.userDisplayName}</span>
                  <span className="text-xs text-surface-500">{label.toLowerCase()}</span>
                </div>

                {log.fieldsChanged.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {log.fieldsChanged.map(f => (
                      <span key={f}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-surface-100 text-surface-500">
                        {f}
                      </span>
                    ))}
                  </div>
                )}

                <p className="text-[11px] text-surface-400 mt-0.5">
                  {formatDisplayDate(log.timestamp)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
